<i18n>
{
	"en": {
		"fromDate": "From",
		"loading": "Loading...",
		"search": "Search",
		"searchForDeviations": "Search for deviations",
		"toDate": "To",
		"deviationNumber": "Deviation Number",
		"failedToFetch": "Failed to search for deviations",
		"customerFault": "Customer Fault",
		"notCustomerFault": "Not Customer Fault"
	},
	"no": {
		"fromDate": "Fra",
		"loading": "Henter avvik...",
		"search": "Søk",
		"searchForDeviations": "Søk etter avvik",
		"toDate": "Til",
		"deviationNumber": "Avviksnummer",
		"unsuccessful": "Bomtur",
		"failedToFetch": "Kunne ikke søke etter avvik",
		"customerFault": "Kundefeil",
		"notCustomerFault": "Ikke Kundefeil"
	}
}
</i18n>

<template>
	<t-vertical-panel>
		<div slot="header" class="deviation-search-header">
			<t-panel-header
				:text="$t('searchForDeviations')"
				icon="search"
				type="bp"
			/>
			<t-button
				:value="deviationSearchText"
				:disabled="deviationsLoading"
				appearance="primary"
				@click="searchDeviations"
			/>
		</div>

		<div class="deviation-search-content">
			<t-form-group horizontal>
				<t-date-picker
					:label="$t('fromDate')"
					:value="formatDate(searchFilter.date.from)"
					:dateFormat="displayDateFormat"
					width="100%"
					relative
					@input="setDateFrom"
					@enter="searchDeviations"
				/>
				<t-date-picker
					:label="$t('toDate')"
					:value="formatDate(searchFilter.date.to)"
					:dateFormat="displayDateFormat"
					width="100%"
					relative
					@input="setDateTo"
					@enter="searchDeviations"
				/>
			</t-form-group>

			<t-number-input
				:label="$t('deviationNumber')"
				:value="searchFilter.deviationNumber"
				@input="setDeviationNumber"
			/>

			<ClerkInput
				:value="searchFilter.clerk"
				focusNextOnSelected
				@change="setClerk"
			/>

			<DeviationStatusInput
				:value="searchFilter.status"
				@change="setStatus"
			/>
			<DeviationTypeInput
				:value="searchFilter.deviationType"
				@change="setDeviationType"
			/>
			<DeviationCauseInput
				:value="searchFilter.deviationCause"
				@change="setDeviationCause"
			/>
			<ActionTypeInput
				:value="searchFilter.actionType"
				@change="setActionType"
			/>

			<TripAddressInput
				title="Terminal"
				:value="searchFilter.terminal || {}"
				focusNextOnSelected
				onlyTerminals
				@change="setTerminal"
			/>

			<t-form-group >
				<GoodsTypesSelect
					:selectedItems="searchFilter.goodsTypes"
					@change="addGoodsType"
				/>
				<t-tag-container
					v-if="hasItems(searchFilter.goodsTypes)"
					:items="searchFilter.goodsTypes"
					displayKey="name"
					@change="setGoodsTypes"
				/>
			</t-form-group>

			<VendorsMultiSelect
				:selectedItems="searchFilter.vendors"
				@change="setVendors"
			/>

			<CustomersSelect
				:selectedItems="searchFilter.customers"
				@change="setCustomers"
			/>

			<t-form-group>
				<t-three-way-switch
					:labelBefore="$t('notCustomerFault')"
					:label="$t('customerFault')"
					:value="searchFilter.customerFault"
					@change="setCustomerFault"
				/>
			</t-form-group>

		</div>
	</t-vertical-panel>
</template>

<script>
import { mapDynamicState, mapDynamicActions } from '@/core/mapDynamic';
import {
	eventBus, displayDateFormat, displayDateFormatter, keydownController,
} from '@/core';

import ClerkInput from '@/modules/clerks/ClerkInput.vue';
import DeviationStatusInput from '@/modules/newDeviation/components/DeviationStatusInput';
import DeviationTypeInput from '@/modules/deviations/DeviationTypeInput.vue';
import DeviationCauseInput from '@/modules/deviations/DeviationCauseInput.vue';
import ActionTypeInput from '@/modules/newDeviation/components/ActionTypeInput';
import TripAddressInput from '@/modules/tripAddresses/TripAddressInput';

import GoodsTypesSelect from '@/modules/goodsTypes/GoodsTypesSelect';
import VendorsMultiSelect from '@/modules/vendors/VendorsMultiSelect';
import CustomersSelect from './components/CustomersSelect';

export default {
	name: 'DeviationSearchFilters',
	components: {
		ClerkInput,
		DeviationStatusInput,
		DeviationTypeInput,
		DeviationCauseInput,
		TripAddressInput,
		ActionTypeInput,
		GoodsTypesSelect,
		VendorsMultiSelect,
		CustomersSelect,
	},

	data() {
		return {
			saveSearchVisible: false,
		};
	},

	computed: {
		...mapDynamicState('computedStoreNamespace', [
			'searchFilter',
			'deviationsLoading',
			'loadedSearch',
		]),
		deviationSearchText() {
			return this.deviationsLoading ? this.$t('loading') : this.$t('search');
		},
		searchId() {
			return this.$route.params.searchId || -1;
		},
		savedSearch() {
			if (Array.isArray(this.savedSearches))
				return this.savedSearches.find(x => x.id === this.searchId);
			return {};
		},
		computedStoreNamespace() {
			return `deviationSearch/${this.searchId}`;
		},
		displayDateFormat() {
			return displayDateFormat;
		},
	},

	created() {
		eventBus.$on('refreshDeviationList', this.searchDeviations);
		keydownController.addKeyListener('Enter', this.searchDeviations,
			{ ctrlKey: true, shiftKey: false, stop: true, prevent: true });
	},
	mounted() {
		if (this.searchId === this.loadedSearch.id) {
			this.searchDeviations();
		}
	},
	beforeDestroy() {
		eventBus.$off('refreshDeviationList');
		keydownController.removeKeyListener('Enter', this.searchDeviations);
	},

	methods: {
		...mapDynamicActions('computedStoreNamespace', [
			'fetchDeviations',
			'setDateFrom',
			'setDateTo',
			'setDeviationNumber',
			'setClerk',
			'setStatus',
			'setDeviationType',
			'setDeviationCause',
			'setActionType',
			'setTerminal',
			'addGoodsType',
			'setGoodsTypes',
			'setVendors',
			'setCustomers',
			'setCustomerFault',
		]),

		async searchDeviations() {
			try {
				await this.fetchDeviations();
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToFetch')
				});
			}
		},

		formatDate(date) {
			return displayDateFormatter(date);
		},
		hasItems(array) {
			return array && array.length;
		},
	},
};
</script>

<style lang="scss" scoped>
.deviation-search-header {
	display:flex;
	align-items: center;
	justify-content: space-between;
	height: 100%;
	margin: 0px 15px;

	& button {
		padding: 0px 50px;
	}
}

.deviation-search-sub-header {
	display:inline-grid;
	grid-auto-flow: column;
	grid-auto-columns: 1fr;
	column-gap: 20px;
	padding: 0px 15px;
	height: 100%;
	width: calc(100% - 30px);
	align-items: center;
}

.deviation-search-content {
	display: grid;
	grid-auto-rows: minmax(min-content, max-content);
	row-gap: 20px;
	background: white;
	min-height: 100%;
	box-sizing: border-box;
	padding: 10px 15px 10px 15px;
}
</style>
